import { useState, useRef, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { deepEqual, filterObject } from '../01-core/Utilities';
import { Button } from '../02-atoms/Button';
import { ChipProps } from '../02-atoms/Chip';
import { Input } from '../02-atoms/Input';
import {
  DataMap,
  Dropdown,
  DropdownProps,
  DropdownItem,
} from '../02-atoms/Dropdown';
import { Chips } from './Chips';
import { AnimatedDialog } from '../01-core/AnimatedDialog';
import { Icon } from '../02-atoms/Icon';
import { Logo, LogoProps } from '../02-atoms/Logo';

/** Collect all field names a dropdown item controls (recursively). */
const collectNames = (
  values?: DropdownItem[],
  acc: Set<string> = new Set<string>(),
): Set<string> => {
  values?.forEach((value) => {
    if (value.name) acc.add(value.name);
    if (value.values) collectNames(value.values, acc);
  });
  return acc;
};

export interface FilterProps {
  /** Fulltext search placeholder */
  fulltext?: string;
  /** Filter dropdown objects */
  items: DropdownProps[];
  /** The initial data */
  data?: DataMap;
  /** Callback on change */
  onChange?: (data: DataMap) => void;
  /** Additional class */
  className?: string;
  /** Active filters blacklist */
  activeFiltersBlacklist?: string[];
  /** Reset button label */
  reset?: string;
  /** Mobile drawer title and trigger label */
  filterLabel?: string;
  /** Mobile drawer close label */
  close?: string;
  /** Mobile trigger icon (provided by the consumer, e.g. an SVG node) */
  triggerIcon?: ReactNode;
  /** Mobile drawer apply button label */
  apply?: string;
  /** Mobile drawer back label */
  back?: string;
  /** Mobile drawer "show results" button label (filter list level) */
  showResults?: string;
  /** Optional logo shown at the top of the mobile drawer (filter list level) */
  logo?: LogoProps;
}

/** Filter rendering multiple dropdown and active filters as chips */
export const Filter = ({
  fulltext,
  items,
  data,
  onChange,
  className,
  activeFiltersBlacklist,
  reset,
  filterLabel = 'Filter',
  close,
  triggerIcon,
  apply,
  back = 'Back',
  showResults,
  logo,
  ...props
}: FilterProps) => {
  const search = useRef<HTMLInputElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [focusRow, setFocusRow] = useState<number | null>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  let currentData: DataMap = {};
  let currentSelectedData: DataMap = {};

  const [selectedData, setSelectedData] = useState<DataMap>(data || {});
  const [filterData, setData] = useState<DataMap>(data || {});

  const clearData = () => {
    currentData = filterObject(
      { ...data, ...filterData, ...currentData },
      activeFiltersBlacklist || [],
    );
    setData(currentData);
    setSelectedData({ ...currentData });
    setActive({});

    if (onChange) {
      onChange(currentData);
    }
  };

  const updateData = (update: DataMap | null) => {
    update = { ...data, ...filterData, ...currentData, ...update };
    currentData = update;

    setData(currentData);
    setActive(getActive(currentData));

    if (onChange) {
      onChange(currentData);
    }
  };

  const applyPendingSelection = () => {
    if (activeItem === null || deepEqual(selectedData, filterData)) return;

    updateData(selectedData);
  };

  /** Leave the drawer detail and hand the focus back to the row it was opened from */
  const closeDetail = () => {
    setFocusRow(activeItem);
    setActiveItem(null);
  };

  const updateSelectedData = (update: DataMap) => {
    update = { ...(selectedData || {}), ...currentSelectedData, ...update };
    currentSelectedData = update;
    setSelectedData(update);
  };

  items.forEach((item) => {
    if (item?.data) {
      data = { ...data, ...item?.data } as Record<string, any>;
    }
  });

  // map items (and build name → metadata index for active chips)
  const itemMap: Record<
    string,
    [
      string,
      (
        | DropdownItem
        | DropdownItem[]
        | ((
            value: string,
          ) => Promise<{ label: string; value: string; name: string } | null>)
      ),
    ]
  > = useMemo(() => {
    const map: Record<
      string,
      [
        string,
        (
          | DropdownItem
          | DropdownItem[]
          | ((
              value: string,
            ) => Promise<{ label: string; value: string; name: string } | null>)
        ),
      ]
    > = {};

    items.forEach((item) => {
      if (item?.searchIndex && item?.getSearchTerm) {
        map[item.searchIndex] = [item.label, item.getSearchTerm];
      }

      const collect = (entry: DropdownItem, requireType: boolean) => {
        const name = entry?.name as string;

        // A name already claimed by a getSearchTerm resolver keeps it: that
        // entry resolves chip labels asynchronously and is not a value list.
        // Both for the same name is a contradiction, and used to throw (#1777).
        if (typeof map[name]?.[1] === 'function') return;

        if (entry?.type === 'checkbox' || entry?.type === 'radio') {
          if (!map?.[name]) {
            map[name] = [item.label, []];
          }
          (map[name][1] as DropdownItem[]).push(entry);
        } else if (entry?.type || !requireType) {
          map[name] = [item.label, entry];
        }
      };

      item.values?.forEach((child: DropdownItem) => {
        child?.values?.forEach((sub_child: DropdownItem) =>
          collect(sub_child, false),
        );
        collect(child, true);
      });
    });

    return map;
  }, [items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getActive = (update: DataMap = filterData) => {
    const newActive: Record<string, ChipProps> = {};
    const d = update || filterData;
    if (d === null) {
      return {};
    }
    Object.keys(d).forEach((name) => {
      const key = name.replace(/_(from|to)$/, '');
      if (!(key in itemMap)) {
        return;
      }
      if (activeFiltersBlacklist && activeFiltersBlacklist.includes(key)) {
        return;
      }
      let value = d[name];
      const [groupLabel, raw] = itemMap[key];

      if (typeof raw === 'function') {
        if (!value) return;
        if (!Array.isArray(value)) {
          value = [value];
        }

        value.forEach((val) => {
          const actionKey = `${key}.${val}`;
          newActive[actionKey] = {
            label: groupLabel + ': ' + val,
            remove: true,
            onClick: () => {
              const update: Record<string, any> = {};
              const current = d[key as string];
              const arr = Array.isArray(current) ? (current as string[]) : [];
              update[key] = arr.filter((v) => v !== String(val));
              updateData(update);
              updateSelectedData(update);
            },
          };
          raw(val).then((item) => {
            if (item === null) return;
            if (!(actionKey in newActive)) return;
            newActive[actionKey].label = groupLabel + ': ' + item.label;
            setActive({ ...newActive });
          });
        });
        return;
      }

      const itemsArr = Array.isArray(raw) ? raw : [raw];

      itemsArr.forEach((item: any) => {
        let label = '';
        let key = item.name as string;
        if (item.type === 'daterange') {
          const from = d?.[item.name + '_from'];
          const to = d?.[item.name + '_to'];

          if (from) {
            label = new Date(from as string).toLocaleDateString();
            if (to) {
              label += ' – ';
            }
          }
          if (to) {
            label += new Date(to as string).toLocaleDateString();
          }
        } else {
          if (item.type === 'checkbox') {
            // Ensure value is an array for checkbox comparison
            const valueArr = Array.isArray(value)
              ? value
              : value
                ? [value]
                : [];
            key = item.name + '.' + item.value;
            // Check if this checkbox's value is in the selected values
            if (valueArr.includes(item.value)) {
              label = item.label as string;
            }
          } else if (item.value === value) {
            label = item.label as string;
          }
        }

        if (label) {
          newActive[key] = {
            label: groupLabel + ': ' + label,
            remove: true,
            onClick: () => {
              const update: Record<string, any> = {};
              if (item.type === 'checkbox') {
                const current = d[item.name as string];
                const arr = Array.isArray(current) ? (current as string[]) : [];
                update[item.name] = arr.filter((v) => v !== String(item.value));
              } else if (item.type === 'daterange') {
                update[item.name + '_from'] = '';
                update[item.name + '_to'] = '';
              } else {
                update[item.name as string] = null;
              }
              updateData(update);
              updateSelectedData(update);
            },
          };
        }
      });
    });

    if (Object.keys(newActive).length && reset) {
      newActive['__reset__'] = {
        label: reset,
        remove: true,
        onClick: () => {
          clearData();
        },
        discreet: true,
      };
    }

    return newActive;
  };

  const [active, setActive] = useState<Record<string, ChipProps>>({});

  // Sync state when parent data changes
  useEffect(() => {
    if (data !== undefined && !deepEqual(data, filterData)) {
      setData({ ...data });
      setSelectedData({ ...data });
      setActive(getActive(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Hand the focus back to the drawer row after leaving its detail view
  useEffect(() => {
    if (focusRow === null || activeItem !== null) return;

    rowRefs.current[focusRow]?.focus();
    setFocusRow(null);
  }, [focusRow, activeItem]);

  // Recalculate active chips when itemMap changes (e.g., when items load)
  // This handles the case where data is set from URL params before items are available
  const itemMapLength = Object.keys(itemMap).length;
  useEffect(() => {
    if (itemMapLength > 0) {
      setActive(getActive(filterData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemMapLength]);

  const doSearch = (
    e?:
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FormEvent<HTMLFormElement>,
  ) => {
    e?.preventDefault();
    if (search.current) {
      updateData({ fulltext: search.current.value as string });
    }
  };

  const handleSearch = () => {
    doSearch();
  };

  const activeTotal = Object.keys(active).filter(
    (key) => key !== '__reset__',
  ).length;

  const countActiveForItem = (item: DropdownProps) => {
    const names = collectNames(item.values);
    if (item.searchIndex) {
      names.add(item.searchIndex);
    }
    let count = 0;
    Object.entries(filterData || {}).forEach(([key, value]) => {
      const base = key.replace(/_(from|to)$/, '');
      if (!names.has(base)) {
        return;
      }
      if (Array.isArray(value)) {
        count += value.length;
      } else if (value !== null && value !== undefined && value !== '') {
        count += 1;
      }
    });
    return count;
  };

  return (
    <div className={['filter', className].join(' ')} {...props}>
      {fulltext && (
        <form className="filter__fulltext" onSubmit={doSearch}>
          <Input
            ref={search}
            type="search"
            name="fulltext"
            value={(selectedData && (selectedData?.fulltext as string)) || ''}
            onChange={(value: string) => {
              setSelectedData({ ...selectedData, fulltext: value });
            }}
            onBlur={doSearch}
            onSearch={handleSearch}
            placeholder={fulltext}
          />
          <Button icon="search" title={fulltext} submit={true} />
        </form>
      )}
      <div className="filter__toggle-bar">
        <button
          type="button"
          className="btn btn--primary filter__toggle"
          onClick={() => setDrawerOpen(true)}
          aria-haspopup="true"
          aria-expanded={drawerOpen ? 'true' : 'false'}
        >
          {triggerIcon && <span className="btn__icon">{triggerIcon}</span>}
          {activeTotal ? `${filterLabel} (${activeTotal})` : filterLabel}
        </button>
        {reset && activeTotal > 0 && (
          <button
            type="button"
            className="filter__reset"
            onClick={() => clearData()}
          >
            {reset}
          </button>
        )}
      </div>
      {items.map((item, i) => {
        return (
          <Dropdown
            key={i}
            data={selectedData}
            appliedData={filterData}
            onChange={updateSelectedData as any}
            onReset={(update: DataMap) => {
              updateSelectedData(update);
              updateData(update);
            }}
            onApply={updateData}
            {...item}
          />
        );
      })}
      <Chips className="filter__active" items={Object.values(active)} />

      <AnimatedDialog
        className="filter__drawer surface--invert"
        open={drawerOpen}
        duration={500}
        onToggle={(state) => {
          setDrawerOpen(state);
          if (!state) {
            setActiveItem(null);
          }
        }}
        aria-label={filterLabel}
      >
        <div className="filter__drawer-top">
          {logo && <Logo className="filter__drawer-logo" {...logo} />}
          <Button
            className="filter__drawer-close"
            type="primary"
            icon="cancel"
            title={close}
            onClick={() => {
              applyPendingSelection();
              setActiveItem(null);
              setDrawerOpen(false);
            }}
          />
        </div>
        <div className="filter__drawer-header">
          {activeItem === null ? (
            <span className="filter__drawer-title">{filterLabel}</span>
          ) : (
            <button
              type="button"
              className="filter__drawer-back"
              onClick={() => {
                applyPendingSelection();
                closeDetail();
              }}
            >
              <Icon icon="arrow_left_triangle" />
              {back}
            </button>
          )}
          {reset && (
            <button
              type="button"
              className="filter__reset"
              onClick={() => clearData()}
            >
              {reset}
            </button>
          )}
        </div>

        {activeItem === null ? (
          <ul className="filter__drawer-list">
            {items.map((item, i) => {
              const count = countActiveForItem(item);
              return (
                <li key={i}>
                  <button
                    type="button"
                    className="filter__drawer-row"
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    onClick={() => setActiveItem(i)}
                  >
                    <span className="filter__drawer-label">
                      {item.label}
                      {count > 0 && (
                        <span className="filter__drawer-count"> ({count})</span>
                      )}
                    </span>
                    <Icon icon="arrow_right_triangle" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="filter__drawer-detail">
            <span className="filter__drawer-title">
              {items[activeItem].label}
            </span>
            <Dropdown
              key={activeItem}
              {...items[activeItem]}
              inline
              data={selectedData}
              appliedData={filterData}
              onChange={updateSelectedData as any}
              onReset={(update: DataMap) => {
                updateSelectedData(update);
                updateData(update);
              }}
              onApply={updateData}
              onClose={closeDetail}
              apply={apply || items[activeItem].apply}
              reset={undefined}
            />
          </div>
        )}

        {activeItem === null && showResults && (
          <div className="filter__drawer-footer">
            <Button
              type="primary"
              size="small"
              label={showResults}
              onClick={() => {
                setActiveItem(null);
                setDrawerOpen(false);
              }}
            />
          </div>
        )}
      </AnimatedDialog>
    </div>
  );
};
